/**
 * @param {Object} IntersectionObserverParameters - Parameters for the intersection observer.
 * @param {Element | NodeListOf<Element>} IntersectionObserverParameters.targets - Targets to observe.
 * @param {function} IntersectionObserverParameters.callback - Callback to run when target is intersecting.
 * @param {object} [IntersectionObserverParameters.options] - Options passed to the IntersectionObserver constructor.
 * @param {boolean} [IntersectionObserverParameters.once] - Boolean to unobserve the target once the target is intersected.
 *
 * @returns {IntersectionObserver} The observer from the `IntersectionObserver` constructor.
 */
export const intersectionObserver = ({ targets = null, callback = null, options = null, once = false }) => {
  if (!targets || (Array.isArray(targets) && !targets.length)) {
    return;
  }

  if (!callback && typeof callback !== 'function') {
    return;
  }

  if (!'IntersectionObserver' in window) {
    callback();
    return;
  }

  const handleTargetIntersection = (entries, observer) =>
    entries.forEach(entry => {
      callback(entry, observer);
      if (once && entry.isIntersecting) observer.unobserve(entry.target);
    });

  const observer = new IntersectionObserver(handleTargetIntersection, options);

  const observableTargets = targets.length ? [...targets] : [targets];
  observableTargets.forEach(target => observer.observe(target));

  return observer;
};

/**
 * https://stackoverflow.com/a/16270434
 */
export const isElementVisible = el => {
  const rect = el.getBoundingClientRect();

  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
    rect.top < (window.innerHeight || document.documentElement.clientHeight)
  );
};
